import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useProjectEntitlements, useUser } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { ModalConfirmation } from '@adsk/alloy-react-modal';
import SelectBox from '@adsk/alloy-select-box';
import FormField from '@adsk/alloy-react-form-field';
import { InfoPopover } from '@adsk/alloy-react-tooltip';
import { AlertInformationIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { useDataApi } from '@adsk/alloy-helpers';
import { useFormikContext } from 'formik';
import {
  FLAG_NAMES,
  SERVICE_ACCESSES,
} from '@adsk/acs-admin-ui-components-helpers';
import { useAdminFeatureFlag } from '@adsk/acs-admin-ui-components-shared-components';
import {
  accessLevelFieldTooltipStyles,
  confirmationModalStyle,
  fieldStyle,
} from '../../styles';
import {
  getProjectUserServicesAccess,
  getProjectUserProductsAccess,
  buildGetProjectAdminsRequest,
  buildRemoveMemberRequest,
  getProjectListPageUrl,
  hasAccessToServices,
} from '../../utils';
import { userPropType } from '../../propTypes';

const {
  ADMIN_FLAGS: { ACC_REPLACE_GET_CONSTRUCTION_APIS },
} = FLAG_NAMES;

const AccessLevelField = ({
  isEmsAccount,
  member,
  onRemove,
  saveMemberData,
}) => {
  const { setFieldValue, values } = useFormikContext();
  const {
    accessLevelChangeConfirmationProps: {
      confirmButtonLabel,
      message,
      onConfirmRemove,
      show,
      title,
    },
    member: {
      selectedAccessLevel,
      services: memberServices,
      products: memberProducts,
    },
    projectServices,
  } = values;
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { currentProject } = useProjectEntitlements();
  const userData = useUser();
  const latestOnRemove = useRef(onRemove);

  const { value: useGetConstructionApis } = useAdminFeatureFlag(
    ACC_REPLACE_GET_CONSTRUCTION_APIS
  );

  const currentUser = userData?.user;
  const currentProjectName = currentProject?.name;
  const disabledAccessLevel = isEmsAccount && member.id === currentUser.id;

  const accessLevels = [
    {
      label: T.translate('PROJECT_MEMBER'),
      value: SERVICE_ACCESSES.MEMBER,
    },
    {
      label: T.translate('PROJECT_ADMIN'),
      value: SERVICE_ACCESSES.ADMIN,
    },
  ];

  // Get project admin members to validate when changing access level
  const {
    data: adminMembersData,
    error: adminMembersError,
    doFetch: doFetchAdminMembers,
  } = useDataApi();
  const {
    data: removeMemberData,
    error: removeMemberError,
    loading: isRemovingMember,
    doFetch: doFetchRemoveMember,
  } = useDataApi();

  const updateMemberAccessLevel = (value) => {
    setFieldValue('member.selectedAccessLevel', { value });
    saveMemberData(
      useGetConstructionApis
        ? {
            products: getProjectUserProductsAccess(memberProducts, value),
          }
        : {
            services: getProjectUserServicesAccess(
              projectServices,
              memberServices,
              value
            ),
          },
      'access_level'
    );
  };

  const showRemoveMemberConfirm = () => {
    setFieldValue('accessLevelChangeConfirmationProps', {
      show: true,
      title: T.translate('REMOVE_MEMBER_MODAL_TITLE'),
      message: T.translate('TURN_OFF_DOCS_CONFIRMATION_BODY_MEMBER'),
      onConfirmRemove: true,
      confirmButtonLabel: T.translate('REMOVE_BUTTON'),
    });
  };

  const showLastAdminConfirm = () => {
    setFieldValue('accessLevelChangeConfirmationProps', {
      show: true,
      title: T.translate('REMOVE_LAST_PROJECT_ADMIN_MODAL_TITLE'),
      message: T.translate('REMOVE_LAST_PROJECT_ADMIN_MODAL_BODY'),
      onConfirmRemove: false,
      confirmButtonLabel: T.translate('REMOVE_BUTTON'),
    });
  };

  const showLastAdminRemoveMemberConfirm = () => {
    setFieldValue('accessLevelChangeConfirmationProps', {
      show: true,
      title: T.translate('REMOVE_LAST_PROJECT_ADMIN_MODAL_TITLE'),
      message: (
        <>
          <div>{T.translate('REMOVE_LAST_PROJECT_ADMIN_MODAL_BODY')}</div>
          <br />
          <div>
            {T.translate('REMOVE_USER_MODAL_BODY', {
              projectName: currentProjectName,
            })}
          </div>
        </>
      ),
      onConfirmRemove: true,
      confirmButtonLabel: T.translate('REMOVE_BUTTON'),
    });
  };

  const showRemoveProjectAdminAccessConfirm = () => {
    setFieldValue('accessLevelChangeConfirmationProps', {
      show: true,
      title: T.translate('REMOVE_PA_ACCESS_CONFIRM_MODAL.TITLE'),
      message: <div>{T.translate('REMOVE_PA_ACCESS_CONFIRM_MODAL.BODY')}</div>,
      onConfirmRemove: false,
      confirmButtonLabel: T.translate('CONFIRM_BUTTON'),
    });
  };

  const handleAccessLevelChangeConfirm = () => {
    updateMemberAccessLevel(SERVICE_ACCESSES.MEMBER);
    setFieldValue('accessLevelChangeConfirmationProps', {
      show: false,
    });
  };

  const handleAccessLevelChangeCancel = () => {
    setFieldValue('accessLevelChangeConfirmationProps', {
      show: false,
    });
  };

  const handleRemoveMemberConfirm = () => {
    doFetchRemoveMember(
      ...buildRemoveMemberRequest(
        currentProject.id,
        member.id,
        useGetConstructionApis
      )
    );
  };

  const handleAccessLevelChange = ({ value }) => {
    // Validate removing admin access to last project admin
    if (value === SERVICE_ACCESSES.MEMBER) {
      if (currentUser.id === member.id) {
        doFetchAdminMembers(
          buildGetProjectAdminsRequest(
            currentProject.id,
            useGetConstructionApis
          )
        );
        return;
      }
      if (!hasAccessToServices(memberServices) && !isEmsAccount) {
        showRemoveMemberConfirm();
        return;
      }
    }
    updateMemberAccessLevel(value);
  };

  const renderInfoPopover = useCallback(
    (props) =>
      disabledAccessLevel && (
        <InfoPopover
          {...props}
          message={T.translate('ACCESS_LEVEL_TOOLTIP_MESSAGE')}
          renderIcon={() => (
            <AlertInformationIcon
              size={16}
              style={accessLevelFieldTooltipStyles}
            />
          )}
          variant={InfoPopover.VARIANTS.DARK}
        />
      ),
    [T, disabledAccessLevel]
  );

  useEffect(() => {
    if (onRemove) latestOnRemove.current = onRemove;
  }, [onRemove]);

  useEffect(() => {
    if (!adminMembersData) return;

    if (
      !adminMembersData.results.some(
        (adminMember) => adminMember.id !== member.id
      )
    ) {
      if (!hasAccessToServices(memberServices)) {
        showLastAdminRemoveMemberConfirm();
      } else {
        showLastAdminConfirm();
      }
    } else if (!hasAccessToServices(memberServices) && !isEmsAccount) {
      showRemoveMemberConfirm();
    } else if (!member.accessLevels.accountAdmin) {
      showRemoveProjectAdminAccessConfirm();
    } else {
      updateMemberAccessLevel(SERVICE_ACCESSES.MEMBER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMembersData]);

  useEffect(() => {
    if (!adminMembersError) return;

    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('CHANGES_NOT_SAVED', {
        field: T.translate('ACCESS_LEVEL_LABEL'),
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMembersError]);

  useEffect(() => {
    if (!removeMemberData) return;

    setFieldValue('accessLevelChangeConfirmationProps', {
      show: false,
    });
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('REMOVE_MEMBER_SUCCESS', {
        name: member.name,
        email: member.email,
      }),
    });
    latestOnRemove.current(true);
    if (currentUser.id === member.id && !member.accessLevels.accountAdmin) {
      window.location.assign(getProjectListPageUrl());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberData]);

  useEffect(() => {
    if (!removeMemberError) return;

    showNotification({
      status: NOTIFICATION_STATUSES.ERROR,
      message: T.translate('CHANGES_NOT_SAVED', {
        field: T.translate('ACCESS_LEVEL_LABEL'),
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeMemberError]);

  return (
    <>
      <FormField
        renderInfoPopover={renderInfoPopover}
        label={T.translate('ACCESS_LEVEL_LABEL')}
        labelVariant={FormField.LABEL_VARIANTS.TOP}
        style={fieldStyle}
      >
        <SelectBox
          disabled={disabledAccessLevel}
          data-testid="AccessLevel_SelectBox"
          onChange={handleAccessLevelChange}
          options={accessLevels}
          style={{ paddingRight: '10px' }}
          value={selectedAccessLevel}
        />
      </FormField>
      <ModalConfirmation
        cancelButtonLabel={T.translate('CANCEL_BUTTON')}
        confirmationButtonLabel={confirmButtonLabel}
        data-testid="AccessLevel_ModalConfirmation"
        disableModalExit
        isConfirmationButtonAlert
        isOnConfirmInProgress={isRemovingMember}
        message={message}
        onClose={handleAccessLevelChangeCancel}
        onConfirm={
          onConfirmRemove
            ? handleRemoveMemberConfirm
            : handleAccessLevelChangeConfirm
        }
        show={!!show}
        style={confirmationModalStyle}
        title={title}
      />
    </>
  );
};

AccessLevelField.propTypes = {
  isEmsAccount: PropTypes.bool,
  member: userPropType.isRequired,
  onRemove: PropTypes.func,
  saveMemberData: PropTypes.func.isRequired,
};

AccessLevelField.defaultProps = {
  isEmsAccount: false,
  onRemove: undefined,
};

export default AccessLevelField;
